import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import styles from './MountainPage.module.css';
import API_BASE_URL from '../config/api';
import { buildEmbedUrl } from '../config/maps';
import { useUser } from '../context/UserContext';
import { buildStorageUrl } from '../config/firebaseStorage';
import RoutePopup from '../components/RoutePopup';
import EditRoutePopup from '../components/EditRoutePopup';
import LinkPopup from '../components/LinkPopup';
import NotePopup from '../components/NotePopup';
import TranslateToggle from '../components/TranslateToggle';
import MountainPageSkeleton from '../components/MountainPageSkeleton';
import { Helmet } from 'react-helmet-async';
import { compressFilesSequentially } from '../utils/imageCompression';
import { useTranslation } from 'react-i18next';
import { auth, storage } from '../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const hasCookieConsent = (storageKey = 'cookieConsent') => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.accepted === true;
  } catch {
    return false;
  }
};

const getTagColor = (tag) => {
  const colors = {
    'Hiking': '#AEC6CF', // Pastel Blue
    'Scrambling': '#F49AC2', // Pastel Pink
    'Glacier travel': '#B39EB5', // Pastel Purple
    'Ice climbing': '#779ECB', // Darker Pastel Blue
    'Via ferrata': '#FFB347' // Pastel Orange
  };
  return colors[tag] || '#dddddd';
};

const LINK_CATEGORY_COLORS = {
  hut: '#B5EAD7',      // Pastel Green
  weather: '#C7CEEA',  // Pastel Blue
  webcam: '#FFDAC1',   // Pastel Peach
  cableCar: '#FFB7B2', // Pastel Salmon
  other: '#F6EAC2'     // Pastel Yellow
};

export default function MountainDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const preloadedMountain = location.state && location.state.mountain ? location.state.mountain : null;
  const { currentUser, addToWishlist, markAsSummited, removeFromWishlist, unmarkSummited } = useUser();
  const { t } = useTranslation();

  const [mountain, setMountain] = useState(preloadedMountain);
  const [loading, setLoading] = useState({ page: !preloadedMountain, wishlist: false, summited: false });
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [newImagesToUpload, setNewImagesToUpload] = useState([]);
  const [isUploadSectionOpen, setIsUploadSectionOpen] = useState(false);
  const [uploadingExtraImages, setUploadingExtraImages] = useState(false);
  const [extraImageError, setExtraImageError] = useState('');
  const [isNotePopupOpen, setIsNotePopupOpen] = useState(false);
  const [isRoutePopupOpen, setIsRoutePopupOpen] = useState(false);
  const [isEditRoutePopupOpen, setIsEditRoutePopupOpen] = useState(false);
  const [isLinkPopupOpen, setIsLinkPopupOpen] = useState(false);
  const [teamUpStartDate, setTeamUpStartDate] = useState('');
  const [teamUpEndDate, setTeamUpEndDate] = useState('');
  const [teamUpLoading, setTeamUpLoading] = useState(false);
  const [teamUpErrorMessage, setTeamUpErrorMessage] = useState('');
  const [teamUpSuccessMessage, setTeamUpSuccessMessage] = useState('');
  const [isTeamUpFormOpen, setIsTeamUpFormOpen] = useState(false);
  const [currentRouteIndex, setCurrentRouteIndex] = useState(0);
  const [routes, setRoutes] = useState([]);

  const [accomCheckIn, setAccomCheckIn] = useState('');
  const [accomCheckOut, setAccomCheckOut] = useState('');

  const [mapConsentAccepted, setMapConsentAccepted] = useState(() => hasCookieConsent());
  const [mapManuallyEnabled, setMapManuallyEnabled] = useState(false);

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  useEffect(() => {
    setImageError(false);
  }, [currentImageIndex, id]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getAirbnbLink = (mountainName) => {
    const baseUrl = `https://www.airbnb.com/s/${encodeURIComponent(mountainName)}/homes`;
    const params = new URLSearchParams({
      tab_id: 'home_tab',
      refinement_paths: '[]/homes'
    });

    // If the user sets dates, include them. If not, keep the link generic.
    if (accomCheckIn) params.set('checkin', accomCheckIn);
    if (accomCheckOut) params.set('checkout', accomCheckOut);

    return `${baseUrl}?${params.toString()}`;
  };

  const getBookingLink = (mountainName) => {
    const baseUrl = 'https://www.booking.com/searchresults.html';
    const params = new URLSearchParams({
      ss: mountainName, // "Search String"
      group_adults: 2,
      no_rooms: 1,
      sb: 1
    });

    // Booking uses "checkin" + "checkout" in many cases; include if provided.
    if (accomCheckIn) params.set('checkin', accomCheckIn);
    if (accomCheckOut) params.set('checkout', accomCheckOut);

    return `${baseUrl}?${params.toString()}`;
  };

  const getGoogleMapsSearchLink = (query) => {
    return `https://www.google.com/maps/search/${encodeURIComponent(query)}`;
  };

  const formatTeamUpMonth = useCallback((value) => {
    if (!value) return t('mountainPage.teamUp.anytime');
    const [year, month] = value.split('-');
    if (!year || !month) return value;
    const date = new Date(Number(year), Number(month) - 1);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  }, [t]);

  const formatTeamUpDate = useCallback((value) => {
    if (!value) return '';
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  const formatTeamUpWindow = useCallback((entry = {}) => {
    if (entry.startDate && entry.endDate) {
      return t('mountainPage.teamUp.windowRange', {
        start: formatTeamUpDate(entry.startDate),
        end: formatTeamUpDate(entry.endDate),
        defaultValue: '{{start}} - {{end}}'
      });
    }
    if (entry.targetMonth) {
      return formatTeamUpMonth(entry.targetMonth);
    }
    return t('mountainPage.teamUp.anytime');
  }, [formatTeamUpDate, formatTeamUpMonth, t]);

  const getMonthLabel = useCallback((month) => {
    if (!month) return '';
    return t(`common.months.${month}`, { defaultValue: month });
  }, [t]);

  const formatSeason = useCallback((season = {}) => {
    const start = season.startMonth ? getMonthLabel(season.startMonth) : '';
    const end = season.endMonth ? getMonthLabel(season.endMonth) : '';
    if (start && end) {
      return t('mountainPage.details.seasonRange', { start, end });
    }
    if (start) {
      return t('mountainPage.details.seasonFrom', { month: start });
    }
    if (end) {
      return t('mountainPage.details.seasonUntil', { month: end });
    }
    return '';
  }, [getMonthLabel, t]);

  const gearProfileLabel = useCallback((profile) => {
    if (!profile) return '';
    return t(`gearProfilesDetailed.${profile}`, { defaultValue: profile });
  }, [t]);

  const renderAccessibilityScore = useCallback((avg, type) => {
    const label = type === 'pt'
      ? t('mountainPage.accessibility.ptLabel')
      : t('mountainPage.accessibility.carLabel');
    if (avg !== null && avg !== undefined) {
      return `${label}: ${avg}`;
    }
    return `${label}: ${t('mountainPage.accessibility.notAvailable')}`;
  }, [t]);

  const buildInitials = (source = '') => {
    if (!source) return '?';
    const parts = source.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return source.slice(0, 2).toUpperCase();
    return parts.slice(0, 2).map(part => part[0]?.toUpperCase() || '').join('');
  };

  const accommodationLinks = useMemo(() => {
    const mountainName = mountain?.name ? `${mountain.name} ${mountain.country || ''}`.trim() : '';
    if (!mountainName) return { airbnb: '', booking: '' };

    return {
      airbnb: getAirbnbLink(mountainName),
      booking: getBookingLink(mountainName)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mountain?.name, mountain?.country, accomCheckIn, accomCheckOut]);

  const teamUps = useMemo(() => {
    return Array.isArray(mountain?.teamUps) ? mountain.teamUps.filter(Boolean) : [];
  }, [mountain?.teamUps]);

  const myTeamUp = useMemo(() => {
    if (!currentUser) return null;
    return teamUps.find(entry => entry.username === currentUser.username) || null;
  }, [teamUps, currentUser?.username]);



  useEffect(() => {
    let isMounted = true;

    const fallbackFetchFromList = async () => {
      try {
        const listRes = await fetch(`${API_BASE_URL}/api/mountains`);
        if (!listRes.ok) {
          const t = await listRes.text();
          throw new Error(`Failed to fetch mountains list. ${listRes.status} ${listRes.statusText}. ${t}`);
        }
        const listData = await listRes.json();
        if (listData.success && Array.isArray(listData.data)) {
          const found = listData.data.find(m => String(m.id) === String(id));
          if (found && isMounted) {
            setMountain(found);
            setCurrentImageIndex(0);
            return true;
          }
        }
      } catch (e) {
        if (isMounted) setError(e.message || 'Network error');
      }
      return false;
    };

    const fetchMountain = async () => {
      // Use preloaded mountain if provided via navigation state
      if (preloadedMountain && isMounted) {
        setMountain(preloadedMountain);
        setCurrentImageIndex(0);
        setLoading(prev => ({ ...prev, page: false }));
        return;
      }

      setLoading(prev => ({ ...prev, page: true }));
      setError('');

      try {
        const res = await fetch(`${API_BASE_URL}/api/mountains/${id}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && isMounted) {
            setMountain(data.data);
            setCurrentImageIndex(0);
          } else if (isMounted) {
            // Try fallback to list when API doesn't provide single
            const ok = await fallbackFetchFromList();
            if (!ok) setError(data.message || 'Failed to fetch mountain');
          }
        } else {
          // Fallback to list for 404 or other non-OK responses
          const ok = await fallbackFetchFromList();
          if (!ok && isMounted) {
            const text = await res.text();
            setError(`Failed to fetch mountain ${id}. ${res.status} ${res.statusText}. ${text}`);
          }
        }
      } catch (e) {
        // On network errors, try the list as a fallback
        const ok = await fallbackFetchFromList();
        if (!ok && isMounted) setError(e.message || 'Network error');
      } finally {
        if (isMounted) setLoading(prev => ({ ...prev, page: false }));
      }
    };

    fetchMountain();
    return () => {
      isMounted = false;
    };
  }, [id, preloadedMountain]);

  // Reset current image index when images change
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [mountain?.images?.length]);

  useEffect(() => {
    const syncConsent = () => setMapConsentAccepted(hasCookieConsent());

    // Initial read (in case something changed between initial render and effect)
    syncConsent();

    // Update when banner changes consent
    window.addEventListener('cookie-consent-changed', syncConsent);

    // Also update when localStorage changes from another tab
    const onStorage = (e) => {
      if (e.key === 'cookieConsent') syncConsent();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('cookie-consent-changed', syncConsent);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    if (id) {
      fetch(`${API_BASE_URL}/api/mountains/${id}/routes`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setRoutes(data.data || []);
          }
        })
        .catch(err => console.error(err));
    }
  }, [id]);

  useEffect(() => {
    if (myTeamUp?.startDate && myTeamUp?.endDate) {
      setTeamUpStartDate(myTeamUp.startDate);
      setTeamUpEndDate(myTeamUp.endDate);
      return;
    }

    if (myTeamUp?.targetMonth) {
      const [year, month] = myTeamUp.targetMonth.split('-').map(Number);
      if (Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12) {
        const start = new Date(year, month - 1, 1);
        const end = new Date(year, month, 0);
        const toIso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        setTeamUpStartDate(toIso(start));
        setTeamUpEndDate(toIso(end));
        return;
      }
    }

    setTeamUpStartDate('');
    setTeamUpEndDate('');
  }, [myTeamUp]);

  useEffect(() => {
    if (!teamUpSuccessMessage) return () => {};
    const timer = setTimeout(() => setTeamUpSuccessMessage(''), 3500);
    return () => clearTimeout(timer);
  }, [teamUpSuccessMessage]);

  const mountainId = mountain?.id;
  const isInWishlist = mountainId ? currentUser?.wishlist?.includes(mountainId) : false;
  const isSummited = mountainId ? currentUser?.summited?.includes(mountainId) : false;

  const goLogin = () => navigate('/login');
  const goSignup = () => navigate('/signup');

  //------------------------------ Accessibility voting helpers -----------------
  const calcAccessibilityAvg = (mapOrObj) => {
    if (!mapOrObj) return null;
    // mapOrObj may be an object mapping username->score
    const entries = Object.entries(mapOrObj).filter(([k,v]) => typeof v === 'number');
    if (entries.length === 0) return null;
    const sum = entries.reduce((s, [,v]) => s + v, 0);
    return +(sum / entries.length).toFixed(2);
  };

  const handleAccessibilityVote = async (type, score) => {
    if (!currentUser || !currentUser.token) { goLogin(); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/accessibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`
        },
        body: JSON.stringify({ type, score })
      });
      const data = await res.json();
      if (data.success) {
        setMountain(data.data);
      } else {
        alert(data.message || t('mountainPage.errors.vote'));
      }
    } catch (e) { console.error('Vote error', e); alert(t('mountainPage.errors.voteNetwork')); }
  };

  const handleToggleWishlist = async () => {
    if (!mountainId) {
      alert(t('mountainPage.alerts.generic'));
      return;
    }
    if (!currentUser) {
      goLogin();
      return;
    }

    setLoading(prev => ({ ...prev, wishlist: true }));
    try {
      let success;
      if (isInWishlist) {
        success = await removeFromWishlist(mountainId);
        alert(success
          ? t('mountainPage.alerts.wishlistRemoved', { name: mountain.name })
          : t('mountainPage.alerts.wishlistRemoveFailed', { name: mountain.name })
        );
      } else {
        success = await addToWishlist(mountainId);
        alert(success
          ? t('mountainPage.alerts.wishlistAdded', { name: mountain.name })
          : t('mountainPage.alerts.wishlistAddFailed', { name: mountain.name })
        );
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      alert(t('mountainPage.alerts.generic'));
    } finally {
      setLoading(prev => ({ ...prev, wishlist: false }));
    }
  };

  const handleToggleSummited = async () => {
    if (!mountainId) {
      alert(t('mountainPage.alerts.generic'));
      return;
    }
    if (!currentUser) {
      goLogin();
      return;
    }

    setLoading(prev => ({ ...prev, summited: true }));
    try {
      let success;
      if (isSummited) {
        success = await unmarkSummited(mountainId);
        alert(success
          ? t('mountainPage.alerts.summitRemoved', { name: mountain.name })
          : t('mountainPage.alerts.summitRemoveFailed', { name: mountain.name })
        );
      } else {
        success = await markAsSummited(mountainId);
        alert(success
          ? t('mountainPage.alerts.summitMarked', { name: mountain.name })
          : t('mountainPage.alerts.summitMarkFailed', { name: mountain.name })
        );
      }
    } catch (error) {
      console.error('Error toggling summited list:', error);
      alert(t('mountainPage.alerts.generic'));
    } finally {
      setLoading(prev => ({ ...prev, summited: false }));
    }
  };

  const handleOpenTeamUpForm = () => {
    if (!currentUser) {
      goLogin();
      return;
    }
    if (currentUser && !currentUser.isVerified) {
      setTeamUpErrorMessage('Please verify your email address to unlock Team Up feature.');
      return;
    }
    setTeamUpErrorMessage('');
    setTeamUpSuccessMessage('');
    setIsTeamUpFormOpen(prev => !prev);
  };

  const handleTeamUpSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser || !currentUser.token) {
      goLogin();
      return;
    }
    if (!mountainId) {
      setTeamUpErrorMessage(t('mountainPage.teamUp.errors.missingMountain'));
      return;
    }


    const preferredStartDate = (teamUpStartDate || '').trim();
    const preferredEndDate = (teamUpEndDate || '').trim();
    if (!preferredStartDate || !preferredEndDate) {
      setTeamUpErrorMessage(t('mountainPage.teamUp.errors.missingDates', { defaultValue: 'Please select both start and end dates.' }));
      return;
    }

    if (preferredEndDate < preferredStartDate) {
      setTeamUpErrorMessage(t('mountainPage.teamUp.errors.invalidDateRange', { defaultValue: 'End date must be on or after start date.' }));
      return;
    }

    const targetMonth = preferredStartDate.slice(0, 7);

    setTeamUpLoading(true);
    setTeamUpErrorMessage('');
    setTeamUpSuccessMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/teamups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`
        },
        body: JSON.stringify({
          preferredStartDate,
          preferredEndDate,
          targetMonth,
          month: targetMonth
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (res.status === 401 || data.message?.toLowerCase().includes('token')) {
          throw new Error(t('mountainPage.teamUp.errors.sessionExpired'));
        }
        throw new Error(data.message || 'Failed to save team up.');
      }
      setMountain(prev => prev ? { ...prev, teamUps: data.data.teamUps } : prev);
      setTeamUpSuccessMessage(t('mountainPage.teamUp.successSaved'));
      setIsTeamUpFormOpen(false);
    } catch (err) {
      const rawMessage = err?.message || '';
      if (rawMessage.toLowerCase().includes('valid month')) {
        setTeamUpErrorMessage(t('mountainPage.teamUp.errors.legacyBackend', {
          defaultValue: 'Server still uses old Team Up validation. Try again in a moment; your date window is being sent with a compatibility fallback.'
        }));
      } else {
        setTeamUpErrorMessage(rawMessage || t('mountainPage.teamUp.errors.save'));
      }
    } finally {
      setTeamUpLoading(false);
    }
  };

  const handleTeamUpRemove = async () => {
    if (!currentUser || !currentUser.token) {
      goLogin();
      return;
    }
    if (!mountainId) return;

    setTeamUpLoading(true);
    setTeamUpErrorMessage('');
    setTeamUpSuccessMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/teamups`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`
        }
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (res.status === 401 || data.message?.toLowerCase().includes('token')) {
          throw new Error(t('mountainPage.teamUp.errors.sessionExpired'));
        }
        throw new Error(data.message || t('mountainPage.teamUp.errors.remove'));
      }
      setMountain(prev => prev ? { ...prev, teamUps: data.data.teamUps } : prev);
      setTeamUpSuccessMessage(t('mountainPage.teamUp.successRemoved'));
      setTeamUpStartDate('');
      setTeamUpEndDate('');
      setIsTeamUpFormOpen(false);
    } catch (err) {
      setTeamUpErrorMessage(err.message || t('mountainPage.teamUp.errors.remove'));
    } finally {
      setTeamUpLoading(false);
    }
  };

  const handleNewImageChange = async (e) => {
    const files = Array.from(e.target.files || []);
    let validFiles = [];
    let hasError = false;

    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        setExtraImageError(t('uploadMountain.errors.fileType', { name: file.name }));
        hasError = true;
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setExtraImageError(t('uploadMountain.errors.fileSize', { name: file.name }));
        hasError = true;
        return;
      }
      validFiles.push(file);
    });

    if (hasError) {
      setNewImagesToUpload([]);
    } else {
      try {
        const compressed = await compressFilesSequentially(validFiles);
        setNewImagesToUpload(compressed);
        setExtraImageError('');
      } catch (compressionError) {
        console.error('Failed to compress extra mountain images', compressionError);
        setNewImagesToUpload(validFiles);
        setExtraImageError(t('uploadMountain.errors.compression'));
      }
    }
  };

  const handleAddNote = async ({ category, content, featureDuration }) => {
    if (!currentUser || !currentUser.token) {
      goLogin();
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`
        },
        body: JSON.stringify({ category, content, featureDuration })
      });

      const data = await res.json();
      if (data.success) {
        // Refresh mountain from server to pick up the new note
        const fresh = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}`);
        const freshJson = await fresh.json();
        if (freshJson.success) setMountain(freshJson.data);
        else console.warn('Failed to refresh mountain after adding note');
      } else {
        alert(data.message || t('mountainPage.errors.addNote'));
      }
    } catch (error) {
      console.error('Error adding note:', error);
      alert(t('mountainPage.errors.addNoteNetwork'));
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!currentUser || !currentUser.token) {
      goLogin();
      return;
    }

    if (!window.confirm(t('mountainPage.prompts.deleteNote'))) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/notes/${encodeURIComponent(noteId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`
        }
      });

      const data = await res.json();
      if (data.success) {
        // Refresh mountain to reflect deletion
        const fresh = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}`);
        const freshJson = await fresh.json();
        if (freshJson.success) setMountain(freshJson.data);
        else console.warn('Failed to refresh mountain after deleting note');
      } else {
        alert(data.message || t('mountainPage.errors.deleteNote'));
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert(t('mountainPage.errors.deleteNoteNetwork'));
    }
  };

  const handleAddRoute = async (routeData) => {
    if (!currentUser || !currentUser.token) {
      goLogin();
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/routes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`
        },
        body: JSON.stringify(routeData)
      });
      const data = await res.json();
      if (data.success && data.data) {
        setRoutes(prev => [...prev, data.data]);
      } else {
        alert(data.message || t('mountainPage.routes.errors.save'));
      }
    } catch (e) {
      console.error('Error adding route:', e);
      alert(t('mountainPage.alerts.generic'));
    }
  };

  const handleEditRoute = async (routeData) => {
    if (!currentUser || !currentUser.token) {
      goLogin();
      return;
    }

    const currentRoute = routes[currentRouteIndex];
    if (!currentRoute) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/routes/${encodeURIComponent(currentRoute.id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`
        },
        body: JSON.stringify(routeData)
      });
      const data = await res.json();
      if (data.success && data.data) {
        setRoutes(prev => prev.map(r => r.id === data.data.id ? data.data : r));
      } else {
        alert(data.message || t('mountainPage.routes.errors.update'));
      }
    } catch (e) {
      console.error('Error updating route:', e);
      alert(t('mountainPage.alerts.generic'));
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!currentUser || !currentUser.token) {
      goLogin();
      return;
    }
    if (!window.confirm(t('mountainPage.routes.deleteConfirm'))) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/routes/${encodeURIComponent(routeId)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${currentUser.token}` }
      });
      const data = await res.json();
      if (data.success) {
        setRoutes(prev => {
          const newRoutes = prev.filter(r => r.id !== routeId);
          // Adjust index if needed
          if (currentRouteIndex >= newRoutes.length && newRoutes.length > 0) {
            setCurrentRouteIndex(Math.max(0, newRoutes.length - 1));
          } else if (newRoutes.length === 0) {
            setCurrentRouteIndex(0);
          }
          return newRoutes;
        });
      } else {
        alert(data.message || t('mountainPage.routes.errors.update'));
      }
    } catch (e) {
      console.error(e);
      alert(t('mountainPage.alerts.generic'));
    }
  };

  const handleAddLink = async (linkData) => {
    if (!currentUser || !currentUser.token) {
      goLogin();
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`
        },
        body: JSON.stringify(linkData)
      });
      const data = await res.json();
      if (data.success && data.data) {
        setMountain(prev => {
           if (!prev) return prev;
           return { ...prev, links: [...(prev.links || []), data.data] };
        });
      } else {
        alert(data.message || t('mountainPage.alerts.generic'));
      }
    } catch (e) {
      console.error('Error adding link:', e);
      alert(t('mountainPage.alerts.generic'));
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!currentUser || !currentUser.token) {
      goLogin();
      return;
    }
    if (!window.confirm(t('mountainPage.usefulLinks.deleteConfirm'))) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/links/${encodeURIComponent(linkId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`
        }
      });
      const data = await res.json();
      if (data.success) {
        setMountain(prev => {
          if (!prev) return prev;
          return { ...prev, links: (prev.links || []).filter(l => l.id !== linkId) };
        });
      } else {
        alert(data.message || t('mountainPage.alerts.generic'));
      }
    } catch (e) {
      console.error('Error deleting link:', e);
      alert(t('mountainPage.alerts.generic'));
    }
  };

  const handleNextRoute = () => {
    // routes derived from State now
    const totalCards = routes.length + 1; // +1 for the "Add Route" card
    setCurrentRouteIndex(prev => (prev + 1) % totalCards);
  };

  const handlePrevRoute = () => {
    const totalCards = routes.length + 1;
    setCurrentRouteIndex(prev => (prev - 1 + totalCards) % totalCards);
  };

  const handleUploadMorePictures = async () => {
    setExtraImageError('');
    if (newImagesToUpload.length === 0) {
      setExtraImageError(t('uploadMountain.errors.images'));
      return;
    }
    if (!currentUser) {
      setExtraImageError(t('uploadMountain.errors.login'));
      goLogin();
      return;
    }

    setUploadingExtraImages(true);
    let uploadedObjects = [];
    let cleanupDone = false;

    const cleanupUploadedObjects = async () => {
      if (cleanupDone || uploadedObjects.length === 0) return;
      cleanupDone = true;
      await Promise.all(
        uploadedObjects.map(({ objectPath }) =>
          deleteObject(storageRef(storage, objectPath)).catch(() => null)
        )
      );
    };

    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Not authenticated');

      const uploadResults = await Promise.allSettled(
        newImagesToUpload.map(async (file, index) => {
          const originalExt = (file.name && file.name.includes('.')) ? file.name.split('.').pop().toLowerCase() : 'jpg';
          const ext = /^[a-z0-9]+$/.test(originalExt) ? originalExt : 'jpg';
          const objectPath = `mountains/${mountainId}/${Date.now()}_${index + 1}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
          const objectRef = storageRef(storage, objectPath);
          await uploadBytes(objectRef, file, { contentType: file.type || 'image/jpeg' });
          const downloadUrl = await getDownloadURL(objectRef);
          return { objectPath, downloadUrl };
        })
      );

      const successfulUploads = uploadResults
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);
      uploadedObjects = successfulUploads;

      const failedUploads = uploadResults.filter(result => result.status === 'rejected');
      if (failedUploads.length > 0) {
        await cleanupUploadedObjects();
        throw new Error('One or more images failed to upload. Please try again.');
      }

      const uploadedUrls = successfulUploads.map(item => item.downloadUrl);

      const response = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/images`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token}`
        },
        body: JSON.stringify({ imageUrls: uploadedUrls })
      });

      if (!response.ok) {
        const errorText = await response.text();
        await cleanupUploadedObjects();
        throw new Error(`Upload failed: ${response.status} ${response.statusText}. ${errorText}`);
      }

      const result = await response.json();
      if (result.success) {
        alert(t('mountainPage.alerts.imageUploadSuccess'));
        setNewImagesToUpload([]);
        setIsUploadSectionOpen(false);

        setMountain(prev => prev
          ? { ...prev, images: [...(prev.images || []), ...result.data.newImageUrls] }
          : prev
        );
        setCurrentImageIndex(0);
      } else {
        await cleanupUploadedObjects();
        setExtraImageError(result.message || t('mountainPage.errors.uploadImages'));
      }
    } catch (error) {
      await cleanupUploadedObjects();
      console.error('Error uploading extra images:', error);
      setExtraImageError(t('mountainPage.errors.uploadImagesNetwork', { message: error.message }));
    } finally {
      setUploadingExtraImages(false);
    }
  };

  const handlePrevImage = () => {
    const len = Array.isArray(mountain?.images) ? mountain.images.length : 0;
    if (len === 0) return;
    setCurrentImageIndex(prevIndex => (prevIndex === 0 ? len - 1 : prevIndex - 1));
  };

  const handleNextImage = () => {
    const len = Array.isArray(mountain?.images) ? mountain.images.length : 0;
    if (len === 0) return;
    setCurrentImageIndex(prevIndex => (prevIndex === len - 1 ? 0 : prevIndex + 1));
  };

  const helmetTitle = mountain?.name ? `${mountain.name} | PoF` : t('mountainPage.helmetTitleFallback');
  const helmetDescription = mountain?.description
    ? mountain.description.slice(0, 150)
    : t('mountainPage.helmetDescriptionFallback');
  const helmet = (
    <Helmet>
      <title>{helmetTitle}</title>
      <meta name="description" content={helmetDescription} />
    </Helmet>
  );

  if (loading.page) {
    return (
      <>
        {helmet}
        <MountainPageSkeleton />
      </>
    );
  }

  if (error) {
    return (
      <>
        {helmet}
        <div className={styles.pageWrapper}>
          <div className={styles.contentContainer}>
            <p className={styles.error}>{error}</p>
            <button onClick={() => navigate(0)}>{t('mountainPage.retry')}</button>
          </div>
        </div>
      </>
    );
  }

  if (!mountain) {
    return (
      <>
        {helmet}
        <div className={styles.pageWrapper}>
          <div className={styles.contentContainer}>
            <div>
              <Link to="/">← {t('mountainPage.back')}</Link>
            </div>
            <p>{t('mountainPage.notFound')}</p>
          </div>
        </div>
      </>
    );
  }

  /* --------------------- RETURN --------------- STATEMENT --------------------------------- */
  return (
    <>
      {helmet}
      <div className={styles.pageWrapper}>
        <div className={styles.contentContainer}>
        <div className={styles.modalBody}>
          {/* ----------------------- Left column: name, actions, image gallery, short data, notes section ----------------- */}
          <div className={styles.modalInfo}>
            <div className={styles.titleRow}>
              <Link to="/browse" className="backButton">
                <span className={styles.backLinkIcon}>←</span>
                {t('mountainPage.back')}
              </Link>
              <h2 className={styles.titleText}>{mountain.name}</h2>
            </div>

            {currentUser && (
              <div className={styles.modalActions}>
                <button
                  onClick={handleToggleWishlist}
                  className={`${styles.btn} ${isInWishlist ? styles.removeBtn : styles.wishlistBtn}`}
                  disabled={loading.wishlist}
                >
                  {loading.wishlist
                    ? (isInWishlist ? t('mountainPage.actions.wishlistRemoving') : t('mountainPage.actions.wishlistAdding'))
                    : (isInWishlist ? t('mountainPage.actions.wishlistRemove') : t('mountainPage.actions.wishlistAdd'))}
                </button>

                <button
                  onClick={handleToggleSummited}
                  className={`${styles.btn} ${isSummited ? styles.removeBtn : styles.summitedBtn}`}
                  disabled={loading.summited}
                >
                  {loading.summited
                    ? (isSummited ? t('mountainPage.actions.summitRemoving') : t('mountainPage.actions.summitMarking'))
                    : (isSummited ? t('mountainPage.actions.summitUnmark') : t('mountainPage.actions.summitMark'))}
                </button>

                <button
                  onClick={() => setIsUploadSectionOpen(prev => !prev)}
                  className={`${styles.btn} ${styles.uploadMoreBtn}`}
                >
                  {isUploadSectionOpen ? t('mountainPage.actions.uploadCancel') : t('mountainPage.actions.uploadMore')}
                </button>

                {(currentUser?.admin || currentUser?.editor) && (
                  <button
                    onClick={() => navigate(`/mountains/${mountainId}/edit`, { state: { mountain } })}
                    className={`${styles.btn} ${styles.editBtn}`}
                  >
                    {t('mountainPage.actions.edit')}
                  </button>
                )}
              </div>
            )}
            {currentUser && isUploadSectionOpen && (
              <div className={styles.uploadExtraImagesSection}>
                <h3>{t('mountainPage.uploadSection.title')}</h3>
                {extraImageError && <div className={styles.error}>{extraImageError}</div>}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleNewImageChange}
                  className={styles.fileInput}
                />
                {newImagesToUpload.length > 0 && (
                  <div className={styles.newImagePreviews}>
                    {newImagesToUpload.map((file, index) => (
                      <div key={index} className={styles.newImagePreviewItem}>
                        <img src={URL.createObjectURL(file)} alt={t('mountainPage.uploadSection.previewAlt', { index: index + 1 })} />
                        <span>{file.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={handleUploadMorePictures}
                  className={styles.uploadExtraBtn}
                  disabled={newImagesToUpload.length === 0 || uploadingExtraImages}
                >
                  {uploadingExtraImages ? t('mountainPage.uploadSection.buttonUploading') : t('mountainPage.uploadSection.buttonSubmit')}
                </button>
              </div>
            )}

            <div className={styles.imageGallery}>
              {Array.isArray(mountain.images) && mountain.images.length > 0 ? (
                (() => {
                  const normalized = mountain.images.map((img, idx) => (
                    typeof img === 'string' ? { id: String(img), url: img, likes: 0, likedBy: [] } : img
                  ));
                  const sorted = normalized.slice().sort((a,b) => (b.likes || 0) - (a.likes || 0));
                  const current = sorted[currentImageIndex] || sorted[0];

                  return (
                    <>
                      <div style={{ position: 'relative' }}>
                        <img
                          src={(current.url && !imageError) ? current.url : buildStorageUrl('other/placeholderM.jpg')}
                          alt={t('mountainPage.imageGallery.alt', { index: currentImageIndex + 1 })}
                          className={styles.mainImage}
                          onError={() => setImageError(true)}
                        />
                        <button
                          className={styles.likeOverlayBtn}
                          onClick={async () => {
                            if (!currentUser || !currentUser.token) { goLogin(); return; }
                            try {
                              const res = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}/images/${encodeURIComponent(current.id)}/like`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${currentUser.token}` }
                              });
                              const data = await res.json();
                              if (data.success) {
                                // refresh mountain
                                const fresh = await fetch(`${API_BASE_URL}/api/mountains/${mountainId}`);
                                const freshJson = await fresh.json();
                                if (freshJson.success) setMountain(freshJson.data);
                              } else {
                                alert(data.message || t('mountainPage.errors.imageLike'));
                              }
                            } catch (e) {
                              console.error('Like error', e);
                              alert(t('mountainPage.errors.imageLikeNetwork'));
                            }
                          }}
                        >
                          {t('mountainPage.imageGallery.likeLabel', { count: current.likes || 0 })}
                        </button>
                      </div>

                      {sorted.length > 1 && (
                        <>
                          <button className={styles.navButtonLeft} onClick={handlePrevImage} aria-label="Previous image">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="15 18 9 12 15 6"></polyline>
                            </svg>
                          </button>
                          <button className={styles.navButtonRight} onClick={handleNextImage} aria-label="Next image">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                          </button>
                        </>
                      )}
                    </>
                  );
                })()
              ) : (
                <div className={styles.noImageFallback}>
                  <div className={styles.placeholderImage}>
                    <img src={buildStorageUrl('other/placeholderM.jpg')} alt={t('mountainPage.imageGallery.placeholderAlt')} />
                  </div>
                </div>
              )}
            </div>

            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <span className={styles.label}>{t('mountainPage.details.country')}:</span>
                <span className={styles.value}>{mountain.country}</span>
              </div>
              {(mountain.accessibility?.publicTransport || mountain.accessibility?.car) && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>{t('mountainPage.accessibility.rowLabel')}:</span>
                  <span className={styles.value}>
                    <span className={styles.accessibilityAverages}>
                      {renderAccessibilityScore(
                        calcAccessibilityAvg(mountain.accessibility?.publicTransport),
                        'pt'
                      )}
                      <span className={styles.sep}> | </span>
                      {renderAccessibilityScore(
                        calcAccessibilityAvg(mountain.accessibility?.car),
                        'car'
                      )}
                    </span>
                  </span>
                </div>
              )}
              <div className={styles.detailItem}>
                <span className={styles.label}>{t('mountainPage.details.height')}:</span>
                <span className={styles.value}>{mountain.height}m</span>
              </div>
              {mountain.uploadedBy && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>{t('mountainPage.details.uploadedBy')}:</span>
                  <span className={styles.value}>
                    {mountain.uploadedBy === '[deleted user]' ? (
                      t('mountainPage.details.deletedUser')
                    ) : (
                      <Link to={`/profile/${encodeURIComponent(mountain.uploadedBy)}`}>
                        @{mountain.uploadedBy}
                      </Link>
                    )}
                  </span>
                </div>
              )}
              {mountain.difficulty && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>{t('mountainPage.details.difficulty')}:</span>
                  <span className={`${styles.value} ${styles.difficultyRating}`}>
                    {mountain.difficulty}
                    <a
                      href="https://www.bergfreunde.eu/alpine-grades-calculator/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.difficultyInfoLink}
                      title={t('mountainPage.details.difficultyInfo')}
                    >
                      ?
                    </a>
                  </span>
                </div>
              )}
              {mountain.gearProfile && (
                  <div className={styles.detailItem}>
                    <span className={styles.label}>{t('mountainPage.details.gearProfile')}:</span>
                    <span className={styles.value}>
                      {gearProfileLabel(mountain.gearProfile)}
                    </span>
                  </div>
                )}
              {mountain.mountainRange && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>{t('mountainPage.details.range')}:</span>
                  <span className={styles.value}>{mountain.mountainRange}</span>
                </div>
              )}
              {mountain.season && (mountain.season.startMonth || mountain.season.endMonth) && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>{t('mountainPage.details.season')}:</span>
                  <span className={styles.value}>{formatSeason(mountain.season)}</span>
                </div>
              )}
            </div>

            <div style={{ marginTop: '2rem' }}>
              {isRoutePopupOpen && (
                <RoutePopup
                  onClose={() => setIsRoutePopupOpen(false)} 
                  onSubmit={handleAddRoute} 
                  mountains={[mountain]} // Pass current mountain as available
                  initialMountainId={mountain.id} // Pre-select
                />
              )}

              {isEditRoutePopupOpen && (
                <EditRoutePopup
                  route={routes[currentRouteIndex]}
                  onClose={() => setIsEditRoutePopupOpen(false)}
                  onSave={handleEditRoute}
                />
              )}

              <div className={styles.routesControl}>
                <button 
                  className={styles.routeNavBtn} 
                  onClick={handlePrevRoute}
                  aria-label="Previous route"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                
                <div className={styles.routesContainer}>
                  
                  {(() => {
                    // State routes used
                    const totalCards = routes.length + 1;
                    
                    // The "Add Route" card is always the last index (routes.length)
                    if (currentRouteIndex === routes.length) {
                      return (
                        <div 
                          className={`${styles.routeCard} ${styles.addRouteCard}`} 
                          onClick={() => setIsRoutePopupOpen(true)}
                          role="button"
                          tabIndex={0}
                        >
                           <div className={styles.addRouteContent}>
                              <span className={styles.plusIcon}>+</span>
                              <span className={styles.addRouteText}>{t('mountainPage.routes.addNew')}</span>
                           </div>
                        </div>
                      );
                    }

                    const route = routes[currentRouteIndex];
                    if (!route) return null;

                    const canEditRoute = currentUser && (
                      currentUser.role === 'admin' ||
                      currentUser.role === 'editor' ||
                      route.createdBy === currentUser.username ||
                      route.author === currentUser.username
                    );

                    return (
                      <div 
                          key={route.id} 
                          className={styles.routeCard}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/routes/${route.id}`)}
                      >
                        <div className={styles.routeCardHeader}>
                          <div>
                            <h3 className={styles.routeTitle} style={{ fontSize: '1.4rem' }}>{route.name}</h3>
                          </div>
                          {canEditRoute && (
                            <span style={{ display: 'flex', gap: '0.25rem' }}>
                              <button
                                className={styles.noteDeleteBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentRouteIndex(routes.findIndex(r => r.id === route.id));
                                  setIsEditRoutePopupOpen(true);
                                }}
                                title={t('mountainPage.routes.edit')}
                              >
                                ✎
                              </button>
                              <button
                                className={styles.noteDeleteBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRoute(route.id);
                                }}
                                title={t('mountainPage.routes.delete')}
                              >
                                ✕
                              </button>
                            </span>
                          )}
                        </div>
                        
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--color3)' }}>
                               {route.kms ? `${route.kms} km` : ''}
                               {route.kms && route.elevationGain ? ' / ' : ''}
                               {route.elevationGain ? `${route.elevationGain}m` : ''}
                            </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <button 
                  className={styles.routeNavBtn} 
                  onClick={handleNextRoute}
                  aria-label="Next route"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>
            </div>

            <div className={styles.accessibilitySection}>
              <h3>{t('mountainPage.accessibility.title', { name: mountain.name })}</h3>
              <div className={styles.voteRow}>
                <div className={styles.voteLabel}>{t('mountainPage.accessibility.publicTransport')}</div>
                <div className={styles.voteControls}>
                  {[1,2,3,4,5].map(score => {
                    const userVote = currentUser ? mountain.accessibility?.publicTransport?.[currentUser.username] : null;
                    const disabled = !currentUser;
                    return (
                      <button
                        key={`pt-${score}`}
                        className={`${styles.voteBtn} ${userVote === score ? styles.voteBtnActive : ''}`}
                        onClick={() => handleAccessibilityVote('publicTransport', score)}
                        disabled={disabled}
                        title={disabled ? t('mountainPage.accessibility.loginToVote') : t('mountainPage.accessibility.vote', { score })}
                      >
                        {score}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={styles.voteRow}>
                <div className={styles.voteLabel}>{t('mountainPage.accessibility.car')}</div>
                <div className={styles.voteControls}>
                  {[1,2,3,4,5].map(score => {
                    const userVote = currentUser ? mountain.accessibility?.car?.[currentUser.username] : null;
                    const disabled = !currentUser;
                    return (
                      <button
                        key={`car-${score}`}
                        className={`${styles.voteBtn} ${userVote === score ? styles.voteBtnActive : ''}`}
                        onClick={() => handleAccessibilityVote('car', score)}
                        disabled={disabled}
                        title={disabled ? t('mountainPage.accessibility.loginToVote') : t('mountainPage.accessibility.vote', { score })}
                      >
                        {score}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={styles.notesSection}>
              {/* --------------------------- NOTES SECTION ----------------------------- */}
              <h3>{t('mountainPage.notes.title')}</h3>
              <button
                onClick={() => setIsNotePopupOpen(prev => !prev)}
                className={`${styles.notesBtn} ${styles.uploadMoreBtn}`}
              >
                {isNotePopupOpen ? t('mountainPage.notes.toggleCancel') : t('mountainPage.notes.toggleAdd')}
              </button>
              <p>{mountain.notes}</p>

              {/* Temporary notes (added by users) */}
              {Array.isArray(mountain.tempNotes) && mountain.tempNotes.length > 0 && (
                <div className={styles.tempNotesList}>
                  {mountain.tempNotes
                    .filter(n => {
                      if (!n) return false;
                      if (!n.expiresAt) return true;
                      return Date.now() <= new Date(n.expiresAt).getTime();
                    })
                    .slice() // copy
                    .reverse() // show newest first
                    .map(note => (
                      <div key={note.id || `${note.createdAt}` } className={styles.noteItem}>
                        <div className={styles.noteHeader}>
                          <strong>{note.author || t('mountainPage.notes.anonymous')}</strong>
                          <span className={styles.noteMeta}>{note.category || t('mountainPage.notes.categoryFallback')}</span>
                          <span className={styles.noteTime}>{new Date(note.createdAt).toLocaleString()}</span>
                          {(currentUser?.admin || currentUser?.editor) && (
                            <button
                              className={styles.noteDeleteBtn}
                              onClick={() => handleDeleteNote(note.id)}
                              title={t('mountainPage.notes.delete')}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <div className={styles.noteContent}>{note.content}</div>
                        {note.expiresAt && (
                          <div className={styles.noteExpires}>{t('mountainPage.notes.expires', { date: new Date(note.expiresAt).toLocaleString() })}</div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {isNotePopupOpen && (
                <NotePopup
                  targetId={mountainId}
                  targetName="Mountain"
                  itemName={mountain?.name}
                  onClose={() => setIsNotePopupOpen(false)}
                  onSubmit={handleAddNote}
                />
              )}
            </div>
          </div>

          {/* ---------------- Right column: description and auth/upload extras ---------------- */}
          <div className={styles.modalInfo}>
            <div className={styles.descriptionSection}>
              <h3>{t('mountainPage.description.title', { name: mountain.name })}</h3>
              <p>{mountain.description}</p>
              <TranslateToggle text={mountain.description || ''} />
            </div>

            {/* Map Section (moved below links) */}
            {!isMobile && (
              <div className={styles.mapSection} style={{ marginTop: '1rem' }}>
                <h3>{t('mountainPage.map.title')}</h3>

                {(mapConsentAccepted || mapManuallyEnabled) ? (
                  <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    <iframe
                      title={t('mountainPage.map.iframeTitle', { name: mountain.name })}
                      src={buildEmbedUrl({ query: `${mountain.name} ${mountain.country} mountain`, zoom: 9 })}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div style={{ borderRadius: '8px', border: '1px solid var(--border-color)', padding: '0.9rem' }}>
                    <p style={{ marginTop: 0, marginBottom: '0.75rem', opacity: 0.9 }}>
                      {t('mountainPage.map.disabled')}
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => setMapManuallyEnabled(true)}
                        className={`${styles.btn} ${styles.uploadMoreBtn}`}
                      >
                        {t('mountainPage.map.loadOnce')}
                      </button>
                      <a
                        href={getGoogleMapsSearchLink(`${mountain.name} ${mountain.country || ''}`.trim())}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.btn} ${styles.wishlistBtn}`}
                        style={{ textDecoration: 'none' }}
                      >
                        {t('mountainPage.map.openGoogle')}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Useful Links Section (moved above map) */}
            <div className={styles.linksSection}>
              <h3>{t('mountainPage.usefulLinks.title')}</h3>
              {(currentUser?.admin || currentUser?.editor) && (
                <button 
                  className={styles.addLinkBtn}
                  onClick={() => setIsLinkPopupOpen(true)}
                >
                  {t('mountainPage.usefulLinks.add')}
                </button>
              )}
              
              <div className={styles.linksList}>
                {(mountain.links || []).map(link => (
                  <a 
                    key={link.id} 
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.linkStripe}
                    style={{ borderLeftColor: LINK_CATEGORY_COLORS[link.category] || LINK_CATEGORY_COLORS.other }}
                  >
                    <span className={styles.linkTitle}>{link.title}</span>
                    {(currentUser?.admin || currentUser?.editor) && (
                      <button
                        className={styles.deleteLinkBtn}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteLink(link.id);
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </a>
                ))}
                {(!mountain.links || mountain.links.length === 0) && (
                  <p className={styles.noLinks}>{t('mountainPage.usefulLinks.noLinks')}</p>
                )}
              </div>
            </div>

            {isLinkPopupOpen && (
              <LinkPopup 
                onClose={() => setIsLinkPopupOpen(false)} 
                onSubmit={handleAddLink} 
              />
            )}

            <div className={styles.mapSection} style={{ marginTop: '1rem' }}>
              <h3>{t('mountainPage.accommodation.title')}</h3>
              <p style={{ marginTop: '0.25rem', marginBottom: '0.75rem', opacity: 0.9 }}>
                {t('mountainPage.accommodation.description', { name: mountain.name })}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label htmlFor="accomCheckIn" style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    {t('mountainPage.accommodation.checkIn')}
                  </label>
                  <input
                    id="accomCheckIn"
                    type="date"
                    value={accomCheckIn}
                    onChange={(e) => setAccomCheckIn(e.target.value)}
                    className={styles.fileInput}
                  />
                </div>

                <div>
                  <label htmlFor="accomCheckOut" style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    {t('mountainPage.accommodation.checkOut')}
                  </label>
                  <input
                    id="accomCheckOut"
                    type="date"
                    value={accomCheckOut}
                    onChange={(e) => setAccomCheckOut(e.target.value)}
                    className={styles.fileInput}
                    min={accomCheckIn || undefined}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <a
                  href={accommodationLinks.airbnb}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.btn} ${styles.uploadMoreBtn}`}
                  style={{ textDecoration: 'none' }}
                >
                  {t('mountainPage.accommodation.airbnb')}
                </a>

                <a
                  href={accommodationLinks.booking}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.btn} ${styles.uploadMoreBtn}`}
                  style={{ textDecoration: 'none' }}
                >
                  {t('mountainPage.accommodation.booking')}
                </a>

                {(accomCheckIn || accomCheckOut) && (
                  <button
                    type="button"
                    onClick={() => {
                      setAccomCheckIn('');
                      setAccomCheckOut('');
                    }}
                    className={`${styles.btn} ${styles.wishlistBtn}`}
                  >
                    {t('mountainPage.accommodation.clearDates')}
                  </button>
                )}
              </div>
            </div>

            <div className={styles.teamUpSection}>
              <div className={styles.teamUpHeader}>
                <h3>{t('mountainPage.teamUp.title')}</h3>
                <button
                  type="button"
                  className={styles.teamUpToggleBtn}
                  onClick={handleOpenTeamUpForm}
                  disabled={teamUpLoading}
                >
                  {!currentUser ? t('mountainPage.teamUp.login') : (myTeamUp ? t('mountainPage.teamUp.update') : t('mountainPage.teamUp.set'))}
                </button>
              </div>
              <p className={styles.teamUpIntro}>
                {t('mountainPage.teamUp.intro')}
              </p>
              {teamUpErrorMessage && <div className={styles.teamUpError}>{teamUpErrorMessage}</div>}
              {teamUpSuccessMessage && <div className={styles.teamUpSuccess}>{teamUpSuccessMessage}</div>}
              {isTeamUpFormOpen && currentUser && (
                <form className={styles.teamUpForm} onSubmit={handleTeamUpSubmit}>
                  <label>{t('mountainPage.teamUp.preferredWindow', { defaultValue: 'Preferred summit window' })}</label>
                  <div className={styles.teamUpDateGrid}>
                    <div className={styles.teamUpDateField}>
                      <label htmlFor="teamUpStartDate">{t('mountainPage.teamUp.startDate', { defaultValue: 'Start date' })}</label>
                      <input
                        id="teamUpStartDate"
                        type="date"
                        value={teamUpStartDate}
                        onChange={(e) => setTeamUpStartDate(e.target.value)}
                        className={styles.fileInput}
                        disabled={teamUpLoading}
                      />
                    </div>
                    <div className={styles.teamUpDateField}>
                      <label htmlFor="teamUpEndDate">{t('mountainPage.teamUp.endDate', { defaultValue: 'End date' })}</label>
                      <input
                        id="teamUpEndDate"
                        type="date"
                        value={teamUpEndDate}
                        onChange={(e) => setTeamUpEndDate(e.target.value)}
                        className={styles.fileInput}
                        min={teamUpStartDate || undefined}
                        disabled={teamUpLoading}
                      />
                    </div>
                  </div>
                  <div className={styles.teamUpFormActions}>
                    <button type="submit" className={styles.teamUpSubmitBtn} disabled={teamUpLoading || !teamUpStartDate || !teamUpEndDate}>
                      {teamUpLoading ? t('mountainPage.teamUp.saving') : t('mountainPage.teamUp.save')}
                    </button>
                    {myTeamUp && (
                      <button
                        type="button"
                        className={styles.teamUpRemoveBtn}
                        onClick={handleTeamUpRemove}
                        disabled={teamUpLoading}
                      >
                        {t('mountainPage.teamUp.remove')}
                      </button>
                    )}
                  </div>
                </form>
              )}
              {!currentUser && (
                <p className={styles.teamUpHint}>{t('mountainPage.teamUp.loginHint')}</p>
              )}
              <div className={styles.teamUpRoster}>
                {teamUps.length === 0 ? (
                  <div className={styles.teamUpEmpty}>{t('mountainPage.teamUp.empty')}</div>
                ) : (
                  <div className={styles.teamUpAvatars}>
                    {teamUps.map((entry) => (
                      <div key={entry.username} className={styles.teamUpAvatar}>
                        {entry.pfp ? (
                          <img src={entry.pfp} alt={entry.nickname || entry.username} />
                        ) : (
                          <div className={styles.teamUpAvatarFallback}>{buildInitials(entry.nickname || entry.username)}</div>
                        )}
                        <div className={styles.teamUpPopover}>
                          <span className={styles.teamUpName}>{entry.nickname || entry.username}</span>
                          <span className={styles.teamUpMonthLabel}>{formatTeamUpWindow(entry)}</span>
                          <Link
                            to={`/chats?user=${encodeURIComponent(entry.username)}`}
                            className={styles.teamUpContactLink}
                          >
                            {t('mountainPage.teamUp.contact', { username: entry.nickname || entry.username })}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {!currentUser && (
              <div className={styles.loginPrompt}>
                <h3>{t('mountainPage.authPrompt.title')}</h3>
                <p>{t('mountainPage.authPrompt.body')}</p>
                <div className={styles.authActions}>
                  <button onClick={goLogin} className={`${styles.btn} ${styles.loginBtn}`}>{t('action.login')}</button>
                  <button onClick={goSignup} className={`${styles.btn} ${styles.signupBtn}`}>{t('action.signup')}</button>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </>
  );
}
